// Localizes win-probability factors through machine codes + params from the
// `crm.win_probability.factor.*` and `crm.deal_detail.reason_*` catalogs. The
// engine's Russian `message` stays the LLM context and the fallback for
// payloads persisted before params and codes existed.
//
// Factor codes deliberately reuse the shared vocabularies: risk-signal codes
// match the deal-risk engine, stage/closed codes match the sales-health
// reasons — both already localized through `crm.deal_detail.reason_*`. Only
// the genuinely new factors (engagement, amount vs median, no-risk) carry
// their own keys.

import type { I18n } from '@govard/i18n-core';

/** Factor shape as it arrives in structured payloads and API responses. */
export interface WinProbabilityFactorLike {
  code: string;
  message: string;
  params?: Record<string, string | number> | undefined;
}

/** Translators the label helpers need — matches the useTranslation() result. */
export type WinProbabilityLabelI18n = Pick<I18n, 't' | 'tc'>;

const FACTOR_KEY_PREFIX = 'crm.win_probability.factor.';
const REASON_KEY_PREFIX = 'crm.deal_detail.reason_';

/** Risk-signal codes the deal-risk engine can emit inside the factor list. */
const SIGNAL_FACTOR_CODES: ReadonlySet<string> = new Set([
  'overdue_next_activity',
  'missing_next_activity',
  'stale_activity',
  'stage_age_over_norm',
  'qualification_gaps',
  'overdue_task',
]);

/** True when the factor mirrors a deal-risk signal code. */
export function isWinProbabilitySignalCode(code: string): boolean {
  return SIGNAL_FACTOR_CODES.has(code);
}

/**
 * Catalog params per factor code: name → expected primitive type. A factor
 * with an unknown code or with missing/ill-typed params falls back to the
 * engine message — interpolation must never render half-empty.
 */
const FACTOR_PARAM_TYPES: Record<string, Readonly<Record<string, 'string' | 'number'>>> = {
  recent_engagement: { count: 'number', days: 'number' },
  amount_above_median: { ratio: 'number' },
  amount_below_median: { ratio: 'number' },
};

function paramsSatisfy(code: string, params: Record<string, string | number> | undefined): boolean {
  const expected = FACTOR_PARAM_TYPES[code];
  if (expected === undefined) return false;
  const source: Record<string, string | number> = params ?? {};
  return Object.entries(expected).every(([name, type]) => typeof source[name] === type);
}

/**
 * Localized factor text. Engagement counts pluralize through tc() (ru
 * one/few/many, en one/other); every other code interpolates through t().
 * Unknown codes and the shared reason codes fall back to the same static
 * `crm.deal_detail.reason_*` labels the deal card already uses.
 */
export function winProbabilityFactorText(
  i18n: WinProbabilityLabelI18n,
  factor: WinProbabilityFactorLike,
): string {
  const params = factor.params;

  if (paramsSatisfy(factor.code, params) && factor.code === 'recent_engagement') {
    const key = FACTOR_KEY_PREFIX + factor.code;
    const translated = i18n.tc(key, params?.count as number, params ?? {});
    return translated === key ? factor.message : translated;
  }

  if (paramsSatisfy(factor.code, params)) {
    const key = FACTOR_KEY_PREFIX + factor.code;
    const translated = i18n.t(key, params ?? {});
    return translated === key ? factor.message : translated;
  }

  // Shared vocabularies (stage anchors, risk signals, terminal outcomes)
  // already have localized reason labels — reuse them instead of duplicating.
  const reasonKey = REASON_KEY_PREFIX + factor.code;
  const reason = i18n.t(reasonKey);
  return reason === reasonKey ? factor.message : reason;
}
